import { compose } from './validation-core';
import { matchRegexp } from './validation-utils';
import { messages, validLength } from './validation-common';

const IPv4SubnetRegexp = /^([0-9]{1,3}\.){3}[0-9]{1,3}(\/([1-9]|[1-2][0-9]|3[0-2]))$/gim;

const IPv6SubnetRegexp =
    /^s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:)))(%.+)?s*(\/([1-9]|[1-9][0-9]|1[0-1][0-9]|12[0-8]))$/gim;

// https://stackoverflow.com/questions/11809631/fully-qualified-domain-name-validation
const domainRegex = /(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{0,62}[a-zA-Z0-9]\.)+[a-zA-Z0-9]{2,63}$)/;

const deviceUuidRegexp = /^([A-Za-z0-9])([A-Za-z0-9-_. :/]*)$/;

const entityNameRegexp = /^[\w-_.]+( [\w-_.]+)*$/;

const memberNameRegexp = /^[\p{L}\p{M}\p{Nd}\-_.]+([\p{L}\p{M}\p{Nd}\-_. ]+)*$/u;

export const subdomainRegex = /^[a-zA-Z0-9]+[a-zA-Z0-9-._]*[a-zA-Z0-9]+$/;

/**
 * Allow dot(.), underscore(_), dash(-) within string
 * Not Allow dot(.), underscore(_), dash(-) in first and last character
 * Allow alphanumeric in string
 */
export const validSubdomain = (subdomain: string) =>
    subdomainRegex.test(subdomain) ? null : messages.INVALID_FORMAT;

export const validHostname = compose(validLength(253, 'Domain'), domain =>
    domainRegex.test(domain) ? null : messages.INVALID_FORMAT,
);

export const validIPv4CIDR = (value: string | null | undefined) =>
    value?.match(IPv4SubnetRegexp) ? null : 'Invalid IPv4 range';

export const validIPv6CIDR = (value: string | null | undefined) =>
    value?.match(IPv6SubnetRegexp) ? null : 'Invalid IPv6 range';

export const validIpCidr = (value: string | null | undefined) =>
    value?.match(IPv4SubnetRegexp) || value?.match(IPv6SubnetRegexp)
        ? null
        : `Invalid Format\nExample: 10.10.10.0/24 for IPv4\nExample: fd00:0:10:10::/64 for IPv6`;

export const validName = (value: string) =>
    matchRegexp(entityNameRegexp, value) ? undefined : messages.INVALID_NAME;

export const validMemberName = (value: string | undefined) =>
    matchRegexp(memberNameRegexp, value) ? undefined : messages.INVALID_NAME;

export const validDeviceUUID = (value: string | null | undefined) => {
    if (!value) {
        return undefined;
    }

    return matchRegexp(deviceUuidRegexp, value) ? undefined : 'Client UUID is not valid';
};

export const validIPMask = (maxIpMask?: number) => (subnet?: string) => {
    if (maxIpMask && subnet && subnet.indexOf('/') !== -1) {
        const mask = Number(subnet.split('/')[1]);

        if (mask > maxIpMask) {
            return `Mask cannot be greater than ${maxIpMask}`;
        }

        return null;
    }

    return null;
};
